import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import { ParkingLotWithExt } from "@onsip/web/features/administrators/parkingLot/parkingLotPage/parking-lot-page.component";
import { ParkingLotFormComponent } from "@onsip/web/features/administrators/parkingLot/parking-lot-form.component";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { MatDialog } from "@angular/material/dialog";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { AdvParkService } from "@onsip/common/services/api/resources/parkingLot/parking-lot.service";
import { map, take } from "rxjs/operators";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { EditAdvParkingLotParams } from "@onsip/common/services/api/resources/parkingLot/parking-lot";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { MusicSourceService } from "@onsip/common/services/api/resources/musicSource/music-source.service";
import { NonNullableFormBuilder } from "@angular/forms";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";

@Component({
  selector: "onsip-caller-settings-edit",
  templateUrl: "./caller-settings-edit.component.html",
  styleUrls: [
    "./caller-settings-edit.component.scss",
    "../../parkingLotShelfContainer/parking-lot-shelf-container.component.scss"
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CallerSettingsEditComponent
  extends ParkingLotFormComponent
  implements OnInit, OnDestroy
{
  @Input() parkLot!: ParkingLotWithExt;
  @Output() editPanel = new EventEmitter<void>();
  mobileView!: Observable<boolean>;
  unsubscriber = new Subscription();
  loadingStateComplete$ = new BehaviorSubject(true);

  constructor(
    private breakpointObserver: BreakpointObserver,
    private snackbar: SnackbarService,
    protected translate: TranslateService,
    protected parkLotService: AdvParkService,
    protected musicSourceService: MusicSourceService,
    protected dialog: MatDialog,
    protected fb: NonNullableFormBuilder,
    protected userService: UserService
  ) {
    super(translate, parkLotService, musicSourceService, dialog, fb, userService);
  }

  ngOnInit(): void {
    this.musicSourceService.musicSourceBrowse();
    this.mobileView = this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
      .pipe(map(res => res.matches));

    for (let i = 1; i <= 10; i++) {
      this.autoReturnTimeout.push((i * 60).toString());
    }

    this.unsubscriber.add(
      this.parkLotForm.controls.pin.valueChanges.subscribe(value => {
        this.parkLotForm.patchValue({
          pinRetrievalEnable: !!value
        });
      })
    );

    this.unsubscriber.add(
      this.parkLotForm.controls.manualReturnDtmf.valueChanges.subscribe(value => {
        this.parkLotForm.patchValue({
          manualReturnEnable: !!value
        });
      })
    );

    this.setParkingLotData();
  }

  setParkingLotData(): void {
    this.parkLotForm.patchValue({
      name: this.parkLot.name,
      channelLimit: this.parkLot.channelLimit,
      pin: this.parkLot.pin,
      parkSipAddress: this.parkLot.address.username,
      extensions: this.parkLot.extensions,
      retrieveSipAddress: this.parkLot.retrieverAddress.username,
      retrieverExtensions: this.parkLot.retrieverExtensions,
      musicSourceName: this.parkLot.musicSourceName,
      musicSourceId: this.parkLot.musicSourceId,
      manualReturnDtmf: this.parkLot.manualReturnDtmf,
      autoReturnTimeout: this.parkLot.autoReturnTimeout,
      returnAddress: this.parkLot.returnAddress
    });
  }

  updateCallerSettings(): void {
    if (this.parkLotForm.invalid) {
      return;
    }
    this.loadingStateComplete$.next(false);
    const params: EditAdvParkingLotParams = {
      Pin: this.parkLotForm.controls.pin.value,
      Username: this.parkLotForm.controls.parkSipAddress.value,
      AdvParkId: this.parkLot.advParkId,
      AutoReturnTimeout: this.parkLotForm.controls.autoReturnTimeout.value,
      ChannelLimit: this.parkLotForm.controls.channelLimit.value,
      ManualReturnDtmf: this.parkLotForm.controls.manualReturnDtmf.value,
      ManualReturnEnable: this.parkLotForm.controls.manualReturnEnable.value,
      MusicSourceId: this.parkLotForm.controls.musicSourceId.value,
      Name: this.parkLotForm.controls.name.value,
      PinRetrievalEnable: this.parkLotForm.controls.pinRetrievalEnable.value
    };
    const returnAddress = this.parkLotForm.controls.returnAddress.value;
    if (returnAddress) {
      params.ReturnAddress = returnAddress;
    }
    this.parkLotService.advParkEdit(params).then(res => {
      if (res && res.status === "success") {
        this.snackbar.openSnackBar("Parking lot successfully updated", "success");
        this.editPanel.emit();
      } else {
        this.snackbar.openSnackBar(res.data.message || "Parking lot was not updated", "error");
        this.parkLotService.clearErrors();
      }
      this.loadingStateComplete$.next(true);
    });
  }

  cancel() {
    const modal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
        message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
        primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.editPanel.emit();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }
}
